import {
  Box,
  IconButton,
  MenuItem,
  Skeleton,
  Snackbar,
  Step,
  StepButton,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { yellow, blue, green, purple, grey } from '@mui/material/colors'
import { ArrowBackOutlined, CalendarMonthOutlined, InfoOutlined } from '@mui/icons-material'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { statusesFor } from 'src/lists'
import DraftLines from './steps/draft/DraftLines'
import ValidateLines from './steps/validate/ValidateLines'
import QuoteStepPlaceholder from './steps/placeholder/QuoteStepPlaceholder'
import { QuoteLinesProvider, useQuoteLines } from './lines/QuoteLinesContext'
import './EditQuote.css'
import { formatCurrency } from 'src/helper'
import dayjs from 'dayjs'
import axios from 'axios'
import ExportQuote from './steps/export/ExportQuote'
import ClosedWon from './steps/closedWon/ClosedWon'
import OrderProcessing from './steps/closedWon/OrderProcessing'
import { SentToCustomer } from './steps/sent/SentToCustomer'

const implementedStatuses = new Set([
  'Draft',
  'Validate',
  'Pending Approval',
  'Approved - Export',
  'Sent to Customer',
  'Closed Won',
  'Won - Draft Order',
  'Won - Order Processing',
  'Closed Lost',
])

function EditQuoteBody() {
  const { quoteId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const lines = useQuoteLines()
  const [activeStep, setActiveStep] = useState(0)
  const [editingVersion, setEditingVersion] = useState(false)
  // Which quoteId we've assigned initial step for (state so ready re-renders when version changes).
  const [initializedFor, setInitializedFor] = useState<string | null>(null)
  const [validateLoading, setValidateLoading] = useState(false)
  const persistedRef = useRef<{ quoteId: string; status: string } | null>(null)
  const statuses = statusesFor(lines.quote.Status)
  const currentStatus = statuses[activeStep]
  const closedLost = statuses.indexOf('Closed Lost')
  const [clipboardSnackbarOpen, setClipboardSnackbarOpen] = useState(false)

  useEffect(() => {
    if (lines.lineItemsLoading || !quoteId || initializedFor === quoteId) return

    if (lines.quote.QuoteId.toLowerCase() !== quoteId.toLowerCase()) {
      navigate(`/quote/${quoteId.toUpperCase()}`)
      return
    }

    // Prefer step from navigate state (e.g. back from TR); otherwise land on quote status.
    const navStep = (location.state as { activeStep?: number } | null)?.activeStep
    setActiveStep(navStep ?? Math.max(0, statusesFor(lines.quote.Status).indexOf(lines.quote.Status)))
    setInitializedFor(quoteId)

    if (navStep != null) {
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [lines.lineItemsLoading, lines.quote.Status, quoteId, initializedFor, location.pathname, location.state, navigate])

  const ready = initializedFor === quoteId

  useEffect(() => {
    if (!ready || !quoteId) return
    const status = statuses[activeStep]
    if (!status || status === 'Closed Won/Lost') return

    const persisted = persistedRef.current
    if (persisted?.quoteId === quoteId && persisted.status === status) return

    persistedRef.current = { quoteId, status }
    if (persisted?.quoteId !== quoteId && status === lines.quote.Status) return

    if (status !== lines.quote.Status) {
      lines.setQuote({ ...lines.quote, Status: status })
    }
    axios.put(`/quote/${quoteId}`, { quote: { Status: status } }).catch(err => {
      console.error(err)
    })
  }, [ready, quoteId, activeStep, lines.quote.Status])

  const handleClosedStatus = (status: 'Closed Won' | 'Closed Lost') => {
    const next = status === 'Closed Won' ? 'Won - Draft Order' : status
    lines.setQuote({ ...lines.quote, Status: next })
    setActiveStep(statusesFor(next).indexOf(next))
  }

  const handleQuoteNumberClick = () => {
    navigator.clipboard.writeText(lines.quote.QuoteNumber).then(() => {
      setClipboardSnackbarOpen(true)
    })
  }

  const handleBack = () => {
    if (activeStep > 0 && lines.quote.Status !== 'Won - Order Processing') {
      setActiveStep(step => step - 1)
      return
    }
    navigate('/quote')
  }

  return (
    <div className="edit-quote">
      <div className="edit-quote-header">
        <IconButton onClick={handleBack} aria-label={activeStep > 0 ? 'Back to previous step' : 'Back to quote list'}>
          <ArrowBackOutlined />
        </IconButton>
        {ready ? (
          <Stepper nonLinear activeStep={activeStep} className="edit-quote-stepper">
            {statuses.map((status, index) => {
              const crossedOut = closedLost >= 0 && index > closedLost
              const gated =
                status === 'Closed Won/Lost' ||
                status === 'Closed Won' ||
                status === 'Won - Draft Order' ||
                status === 'Won - Order Processing' ||
                crossedOut
              return (
                <Step key={status} completed={index < activeStep && !crossedOut}>
                  <StepButton
                    color="inherit"
                    className={crossedOut ? 'step-crossed-out' : undefined}
                    disabled={gated}
                    onClick={async () => {
                      if (gated) return
                      if (index === activeStep + 1 || index < activeStep) {
                        if (status === 'Validate') {
                          try {
                            setValidateLoading(true)
                            await axios.get(`/quote/${quoteId}/validate`)
                          } catch (err) {
                            console.error(err)
                          } finally {
                            setValidateLoading(false)
                          }
                        }
                        setActiveStep(index)
                      }
                    }}
                    disableRipple
                  >
                    {status}
                  </StepButton>
                </Step>
              )
            })}
          </Stepper>
        ) : (
          <Skeleton height={40} width="100%" />
        )}
      </div>
      <div className="lines-header-info">
        <Box display="inline-flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Quote Number:{' '}
          </Typography>
          {lines.lineItemsLoading ? (
            <Skeleton width={120} height={25} />
          ) : (
            <Box display="inline-flex" alignItems="center" gap={1}>
              <Typography
                variant="body1"
                color="text.primary"
                className="clickable"
                onClick={() => handleQuoteNumberClick()}
              >
                {lines.quote.QuoteNumber}
              </Typography>
              <Tooltip
                className="lines-header-info-tooltip"
                arrow
                title={
                  <Box className="lines-header-info-tooltip-content">
                    {[
                      `Quote Date: ${lines.quote.QuoteDate ? dayjs(lines.quote.QuoteDate).format('MM/DD/YYYY') : '-'}`,
                      `Expiration Date: ${
                        lines.quote.ExpirationDate ? dayjs(lines.quote.ExpirationDate).format('MM/DD/YYYY') : '-'
                      }`,
                    ].join('\n')}
                  </Box>
                }
              >
                <CalendarMonthOutlined />
              </Tooltip>
            </Box>
          )}
        </Box>
        <Box display="inline-flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Version:{' '}
          </Typography>
          {lines.lineItemsLoading ? (
            <Skeleton width={120} height={25} />
          ) : editingVersion ? (
            <TextField
              value={lines.quote.QuoteId}
              onChange={e => {
                navigate(`/quote/${e.target.value}`)
                lines.setSelectedLineItems([])
                setEditingVersion(false)
              }}
              size="small"
              select
              autoFocus
              slotProps={{
                select: {
                  open: true,
                  onClose: () => setEditingVersion(false),
                },
              }}
            >
              {lines.versions.map(
                (version: { Name: string | null; Version: number; CreatedDate: string; QuoteId: string }) => (
                  <MenuItem value={version.QuoteId} key={version.QuoteId}>
                    {lines.quote.QuoteNumber}.{String(version.Version).padStart(2, '0')}{' '}
                    {version.Name ? `- ${version.Name}` : ''} ({dayjs(version.CreatedDate).format('MM/DD/YYYY hh:mm A')}
                    )
                  </MenuItem>
                ),
              )}
            </TextField>
          ) : (
            <Tooltip
              title={`${lines.quote.QuoteNumber}.${String(lines.quote.Version).padStart(2, '0')}${
                lines.quote.Name ? ` - ${lines.quote.Name}` : ''
              } (${dayjs(lines.quote.CreatedDate).format('MM/DD/YYYY hh:mm A')})`}
            >
              <Typography className="lines-header-info-version" onClick={() => setEditingVersion(true)}>
                {`${lines.quote.QuoteNumber}.${String(lines.quote.Version).padStart(2, '0')}`}
              </Typography>
            </Tooltip>
          )}
        </Box>
        <Box display="inline-flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Contract Vehicle:{' '}
          </Typography>
          {lines.lineItemsLoading ? (
            <Skeleton width={120} height={25} />
          ) : (
            <Typography variant="body1" color="text.primary">
              {lines.quote.ContractVehicle}
            </Typography>
          )}
        </Box>
        <Box display="inline-flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Subtotal:{' '}
          </Typography>
          {lines.lineItemsLoading ? (
            <Skeleton width={120} height={25} />
          ) : (
            <Typography variant="body1" color="text.primary">
              {formatCurrency(lines.quote.Subtotal)}
            </Typography>
          )}
        </Box>
        <Box display="inline-flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Total Cost:{' '}
          </Typography>
          {lines.lineItemsLoading ? (
            <Skeleton width={120} height={25} />
          ) : (
            <Typography variant="body1" color="text.primary">
              {formatCurrency(lines.quote.TotalCost)}
            </Typography>
          )}
        </Box>

        <Box display="inline-flex" alignItems="center" gap={1}>
          <Tooltip
            className="lines-header-info-tooltip"
            arrow
            title={
              <Box className="lines-header-info-tooltip-content">
                {[
                  `Credit Card Fees: ${formatCurrency(lines.quote.CreditCardFees)}`,
                  `Total Partner Fees: ${formatCurrency(lines.quote.PartnerFees)}`,
                  `Total Contract Fees: ${formatCurrency(lines.quote.ContractFees)}`,
                  `Total Fees: ${formatCurrency(lines.quote.TotalFees)}`,
                ].join('\n')}
              </Box>
            }
          >
            <InfoOutlined />
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            Total Fees:{' '}
          </Typography>
          {lines.lineItemsLoading ? (
            <Skeleton width={120} height={25} />
          ) : (
            <Typography variant="body1" color="text.primary">
              {formatCurrency(lines.quote.TotalFees)}
            </Typography>
          )}
        </Box>
        <Box display="inline-flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Gross Profit:{' '}
          </Typography>
          {lines.lineItemsLoading ? (
            <Skeleton width={120} height={25} />
          ) : (
            <Typography variant="body1" color="text.primary">
              {formatCurrency(lines.quote.GrossProfit)}
            </Typography>
          )}
        </Box>
      </div>
      <div className="edit-quote-content">
        {ready && currentStatus === 'Draft' && (
          <DraftLines
            validateLoading={validateLoading}
            setValidateLoading={setValidateLoading}
            setView={setActiveStep}
          />
        )}
        {ready && (currentStatus === 'Validate' || currentStatus === 'Pending Approval') && <ValidateLines />}
        {ready && currentStatus === 'Approved - Export' && <ExportQuote />}
        {ready && (currentStatus === 'Sent to Customer' || currentStatus === 'Closed Lost') && (
          <SentToCustomer onClosed={handleClosedStatus} />
        )}
        {ready && (currentStatus === 'Closed Won' || currentStatus === 'Won - Draft Order') && (
          <ClosedWon
            onSubmitted={() => {
              setActiveStep(statusesFor('Won - Order Processing').indexOf('Won - Order Processing'))
            }}
          />
        )}
        {ready && currentStatus === 'Won - Order Processing' && <OrderProcessing />}
        {ready && !implementedStatuses.has(currentStatus) && <QuoteStepPlaceholder status={currentStatus} />}
      </div>
      <Snackbar
        open={clipboardSnackbarOpen}
        onClose={() => setClipboardSnackbarOpen(false)}
        message="Quote number copied to clipboard"
        autoHideDuration={2000}
      />
    </div>
  )
}

export function quoteStatusColor(status: string) {
  switch (status) {
    case 'Draft':
      return grey[300]
    case 'Validate':
      return yellow[300]
    case 'Approved - Export':
      return blue[100]
    case 'Sent to Customer':
      return purple[100]
    case 'Closed Won':
    case 'Won - Draft Order':
    case 'Won - Order Processing':
      return green[100]
    case 'Closed Lost':
    case 'Closed Won/Lost':
    case 'Closed':
      return grey[300]
    default:
      return grey[300]
  }
}

export default function EditQuote() {
  return (
    <QuoteLinesProvider>
      <EditQuoteBody />
    </QuoteLinesProvider>
  )
}
